import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import {
  businessHistory,
  inspectBusinessIntent,
  inspectBusinessController,
  validateBusinessCommand,
  businessPreparationBody,
  businessCancellationBody,
  businessReceipt,
  postBusiness,
  recoverBusiness,
  saveBusinessReference,
  type BusinessReference,
} from './businessWorkflow'
import type { Data } from './workflow'

export function useBusinessWorkflow(allowed: boolean) {
  const { user } = useAuth(),
    reviewer = user?.id ?? ''
  const identity = `${reviewer}:${user?.authScope}:${user?.role}:${user?.tenantId}:${allowed}`
  const current = useRef(identity)
  current.current = identity
  const epoch = useRef(0),
    flight = useRef(false)
  const [references, setReferences] = useState<BusinessReference[]>([]),
    [busy, setBusy] = useState(false),
    [broken, setBroken] = useState(false),
    [error, setError] = useState(''),
    [view, setView] = useState<Data | null>(null)
  useEffect(() => {
    epoch.current++
    flight.current = false
    setBusy(false)
    setView(null)
    setError('')
    setBroken(false)
    setReferences([])
    try {
      if (reviewer && allowed) setReferences(businessHistory(reviewer))
    } catch {
      setBroken(true)
      setError('Original business history is unavailable. Restore it before writing.')
    }
    return () => {
      epoch.current++
    }
  }, [identity, reviewer, allowed])
  async function run(ref: BusinessReference, body?: Data, retry = false) {
    if (!allowed || !reviewer || current.current !== identity || flight.current || broken) return
    flight.current = true
    const owner = identity,
      version = ++epoch.current
    const live = () => current.current === owner && epoch.current === version
    setBusy(true)
    setError('')
    setView(null)
    try {
      if (body) {
        validateBusinessCommand(ref, body)
        const pending = businessHistory(reviewer).filter(r => r.status === 'pending')
        const reconciliation =
          ref.kind === 'reconcile' &&
          pending.every(
            r =>
              ['issue', 'cancel', 'reconcile'].includes(r.kind) &&
              r.target === ref.target &&
              r.command === ref.command
          )
        const sameOriginalRetry =
          retry &&
          ['prepare', 'cancel'].includes(ref.kind) &&
          pending.every(
            r => r.kind === ref.kind && r.target === ref.target && r.command === ref.command
          )
        if (pending.length && !reconciliation && !sameOriginalRetry)
          throw new Error('Check the pending original outcome before another write.')
        saveBusinessReference(reviewer, { ...ref, status: 'pending' })
        if (live()) setReferences(businessHistory(reviewer))
      }
      const row = businessReceipt(
        body ? await postBusiness(ref, body) : await recoverBusiness(ref),
        ref,
        reviewer
      )
      if (!live()) return
      saveBusinessReference(reviewer, { ...ref, status: 'confirmed' })
      if (ref.kind === 'reconcile')
        for (const r of businessHistory(reviewer))
          if (
            ['issue', 'cancel'].includes(r.kind) &&
            r.target === ref.target &&
            r.command === ref.command
          )
            saveBusinessReference(reviewer, { ...r, status: 'confirmed' })
      if (live()) {
        setReferences(businessHistory(reviewer))
        setView(row)
      }
      return row
    } catch (e) {
      if (live())
        setError(
          (e instanceof Error ? e.message : 'Business outcome unavailable') +
            ' Keep original references;an unavailable response does not prove no effect.'
        )
    } finally {
      if (live()) {
        flight.current = false
        setBusy(false)
      }
    }
  }
  async function readIntent(intent: string, controller = false) {
    if (!allowed || !reviewer || current.current !== identity || flight.current || broken) return
    flight.current = true
    const owner = identity,
      version = ++epoch.current
    setBusy(true)
    setError('')
    setView(null)
    try {
      const result = await (controller ? inspectBusinessController : inspectBusinessIntent)(
        intent,
        reviewer
      )
      if (current.current !== owner || epoch.current !== version) return
      saveBusinessReference(reviewer, result.ref)
      setReferences(businessHistory(reviewer))
      setView(result.row)
      return result
    } catch {
      if (current.current === owner && epoch.current === version)
        setError(
          'Original business intent unavailable. Keep original references;no write was submitted.'
        )
    } finally {
      if (current.current === owner && epoch.current === version) {
        flight.current = false
        setBusy(false)
      }
    }
  }
  return {
    readIntent,
    readController: (intent: string) => readIntent(intent, true),
    references,
    busy,
    broken,
    error,
    view,
    locked: busy || broken || references.some(r => r.status === 'pending'),
    run,
    retryCancellation: (ref: BusinessReference) => run(ref, businessCancellationBody(ref), true),
    retryPreparation: (ref: BusinessReference) => run(ref, businessPreparationBody(ref), true),
  }
}
